import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Transition } from '@headlessui/react';
import Modal from '../../components/Modal';
import AddVariant from './admin-components/AddVariant';
import DeleteVariant from './admin-components/DeleteVariant';
import VariantController from './controllers/variant-controller';
import EditVariant from './admin-components/EditVariant';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { usePermissionViewModel } from './permissions/viewmodel/permission-viewmodel';

const VariantsPage = () => {
  // Existing state variables
  const [variants, setVariants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedVariants, setExpandedVariants] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Replace the existing const [pageSize] = useState(10);
  const [pageSizeOptions] = useState([5, 10, 25, 50]);
  const [totalItems, setTotalItems] = useState(0);
  const [maxPage, setMaxPage] = useState(1);
  const [isAddVariantModalOpen, setIsAddVariantModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const {hasPermission} = usePermissionViewModel('variante')
  
  // New state variables for deletion
  const [isDeleteVariantModalOpen, setIsDeleteVariantModalOpen] = useState(false);
  const [variantToDelete, setVariantToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // New state variables for editing
  const [isEditVariantModalOpen, setIsEditVariantModalOpen] = useState(false);
  const [variantToEdit, setVariantToEdit] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // New state variable for debounced search term
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  // Debounce the search term to avoid excessive API calls
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // Wait 500ms after user stops typing before sending request
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch variants using controller
  // Inside your useEffect for fetching variants:

useEffect(() => {
  const fetchVariants = async () => {
    setLoading(true);
    try {
      // Pass the current page to the API
      const response = await VariantController.fetchVariants(currentPage, pageSize);
      
      setVariants(response.list || []); 
      setTotalItems(response.total || 0);
      setMaxPage(response.maxPage || 1);
      
      // Log the response to verify we're getting correct data
      console.log("Server response:", response);
    } catch (err) {
      setError("Erreur lors du chargement des variantes");
      console.error("Error fetching variants:", err);
    } finally {
      setLoading(false);
    }
  };

  fetchVariants();
}, [currentPage, pageSize]); // This ensures fetchVariants runs when page or pageSize changes

  // Filter variants based on search term (client-side filtering for UX)
  const filteredVariants = useMemo(() => {
    if (!searchTerm.trim()) {
      return variants;
    }
    
    const lowerCaseSearch = searchTerm.toLowerCase().trim();
    return variants.filter(variant => 
      variant.name.toLowerCase().includes(lowerCaseSearch)
    );
  }, [variants, searchTerm]);

  // Toggle variant expansion
  const toggleVariantExpansion = (variantId) => {
    setExpandedVariants(prev => ({
      ...prev,
      [variantId]: !prev[variantId]
    }));
  };

  // Create new variant
  const handleCreateVariant = () => {
    setIsAddVariantModalOpen(true);
  };

  // Handle submit of new variant
  const handleSubmitNewVariant = async (variantData) => {
    setIsSubmitting(true);
    try {
      // Use the controller to create variant
      const response = await VariantController.createVariant(variantData);
      
      // Check the response structure and update the state accordingly
      if (response) {
        // Get the created variant from the response
        const newVariant = response.entet || response;
        
        // Update local state with new variant
        setVariants(prev => [...prev, newVariant]);
        setTotalItems(prev => prev + 1);
        
        // Close modal and show success message with toast
        setIsAddVariantModalOpen(false);
        toast.success(`Variante "${variantData.name}" créée avec succès!`, {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      } else {
        throw new Error("Réponse invalide du serveur");
      }
    } catch (error) {
      console.error('Error creating variant:', error);
      toast.error(`Erreur lors de la création de la variante: ${error.message || "Erreur inconnue"}`, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Open delete variant modal
  const handleOpenDeleteModal = (variant) => {
    setVariantToDelete(variant);
    setIsDeleteVariantModalOpen(true);
  };
  
  // Delete variant implementation
  const handleConfirmDeleteVariant = async () => {
    if (!variantToDelete) return;
    
    setIsDeleting(true);
    try {
      // Call the API to delete the variant
      await VariantController.deleteVariant(variantToDelete.id);
      
      // Update local state after deletion
      setVariants(variants.filter(variant => variant.id !== variantToDelete.id));
      setTotalItems(prev => prev - 1);
      
      // Store name before closing modal
      const deletedName = variantToDelete.name;
      
      // Close modal and clear state
      setIsDeleteVariantModalOpen(false);
      setVariantToDelete(null);
      
      // Show success toast notification
      toast.success(`Variante "${deletedName}" supprimée avec succès!`, {
        position: "top-right",
        autoClose: 3000,
        icon: "🗑️",
      });
    } catch (error) {
      console.error('Error deleting variant:', error);
      toast.error(`Erreur lors de la suppression de la variante: ${error.message || "Erreur inconnue"}`, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Delete variant - open the confirmation modal
  const handleDeleteVariant = (variantId) => {
    const variant = variants.find(v => v.id === variantId);
    if (variant) {
      handleOpenDeleteModal(variant);
    }
  };
  
  // Delete variant value
  const handleDeleteVariantValue = async (valueId, variantId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette valeur de variante ?")) {
      try {
        // Use controller to delete variant value
        await VariantController.deleteVariantValue(variantId, valueId);
        
        // Find the value that was deleted to show in the toast
        const variant = variants.find(v => v.id === variantId);
        const deletedValue = variant?.values.find(v => v.id === valueId)?.value || 'Valeur';
        
        // Update local state after deletion
        const updatedVariants = variants.map(variant => {
          if (variant.id === variantId) {
            return {
              ...variant,
              values: variant.values.filter(value => value.id !== valueId)
            };
          }
          return variant;
        });
        
        setVariants(updatedVariants);
        
        // Success notification with toast
        toast.success(`Valeur "${deletedValue}" supprimée avec succès!`, {
          position: "top-right",
          autoClose: 3000,
        });
      } catch (error) {
        console.error('Error deleting variant value:', error);
        toast.error('Erreur lors de la suppression de la valeur.', {
          position: "top-right",
          autoClose: 5000,
        });
      }
    }
  };
  
  // Add new value to variant
  const handleAddVariantValue = (variantId) => {
    // This would typically open a modal to add a value
    // For now, just a placeholder implementation
    const newValue = prompt("Entrez la nouvelle valeur:");
    if (newValue && newValue.trim()) {
      addVariantValue(variantId, newValue.trim());
    }
  };
  
  // Implementation of adding a value
  const addVariantValue = async (variantId, value) => {
    try {
      // Use controller to add value
      const newValue = await VariantController.addVariantValue(variantId, value);
      
      // Update local state with new value
      const updatedVariants = variants.map(variant => {
        if (variant.id === variantId) {
          return {
            ...variant,
            values: [...variant.values, newValue]
          };
        }
        return variant;
      });
      
      setVariants(updatedVariants);
      
      // Success notification with toast
      toast.success(`Valeur "${value}" ajoutée avec succès!`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (error) {
      console.error('Error adding variant value:', error);
      toast.error('Erreur lors de l\'ajout de la valeur.', {
        position: "top-right",
        autoClose: 5000,
      });
    }
  };

  const handleSubmitForm = () => {
    // Get form values from AddVariant and submit them
    const formValues = document.getElementById('addVariantForm');
    if (formValues) {
      const submitEvent = new Event('submit', { cancelable: true, bubbles: true });
      formValues.dispatchEvent(submitEvent);
    }
  };

  // Open edit variant modal
  const handleOpenEditModal = (variant) => {
    setVariantToEdit(variant);
    setIsEditVariantModalOpen(true);
  };

  // Handle edit variant button click
  const handleEditVariant = (variantId) => {
    const variant = variants.find(v => v.id === variantId);
    if (variant) {
      handleOpenEditModal(variant);
    }
  };

  // Handle submit of edited variant
  const handleSubmitEditVariant = async (updatedVariantData) => {
    console.log("Updated variant data:", updatedVariantData);
    setIsEditing(true);
    try {
      // Find the original variant to compare values
      const originalVariant = variants.find(v => v.id === updatedVariantData.id);
      
      if (!originalVariant) {
        throw new Error("Original variant not found");
      }
      
      // Process values: include IDs for existing values, none for new values
      const processedData = {
        ...updatedVariantData,
        values: updatedVariantData.values.map(valueItem => {
          // If this is a new value (no ID), don't include ID
          if (!valueItem.id) {
            return {
              value: valueItem.value,
              variantId: updatedVariantData.id
            };
          }
          
          // For all existing values, include the ID regardless of whether they changed
          return {
            id: valueItem.id,
            value: valueItem.value,
            variantId: updatedVariantData.id
          };
        })
      };
      
      // Use the controller to update the variant
      const response = await VariantController.updateVariant(processedData);
      
      // Check the response structure and update the state accordingly
      if (response) {
        // Update local state with the updated variant
        setVariants(prev => 
          prev.map(variant => 
            variant.id === updatedVariantData.id ? response : variant
          )
        );
        
        // Close the modal and reset state
        setIsEditVariantModalOpen(false);
        setVariantToEdit(null);
        
        // Show success notification
        toast.success(`Variante "${updatedVariantData.name}" mise à jour avec succès!`, {
          position: "top-right",
          autoClose: 3000,
        });
      } else {
        throw new Error("La mise à jour a échoué");
      }
    } catch (error) {
      console.error('Error updating variant:', error);
      toast.error(`Erreur lors de la mise à jour de la variante: ${error.message || "Erreur inconnue"}`, {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setIsEditing(false);
    }
  };

  // Trigger the edit form submission
  const handleSubmitEditForm = () => {
    const formValues = document.getElementById('editVariantForm');
    if (formValues) {
      const submitEvent = new Event('submit', { cancelable: true, bubbles: true });
      formValues.dispatchEvent(submitEvent);
    }
  };

  // Add a function to handle page size changes
  const handlePageSizeChange = (newSize) => {
    setPageSize(Number(newSize));
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Update the search input handler to reset to first page when searching
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // No need to reset page if we're just filtering client-side
    // The pagination will adjust automatically based on filtered results
  };

  // Helper function for rendering page numbers with ellipsis
const renderPageNumbers = () => {
  // Early return if maxFilteredPage is 1 or less (no pagination needed)
  if (maxFilteredPage <= 1) {
    return null;
  }
  
  // Function to render a single page button
  const renderPageButton = (pageNum) => (
    <button
      key={pageNum}
      onClick={() => setCurrentPage(pageNum)}
      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium ${
        currentPage === pageNum
          ? 'z-10 bg-[#174540]/30 border-[#174540]'
          : 'text-gray-700 hover:bg-gray-50'
      }`}
      aria-current={currentPage === pageNum ? 'page' : undefined}
    >
      {pageNum}
    </button>
  );

  // If there are no pages or just one, don't render anything
  if (maxFilteredPage <= 1) {
    return null;
  }
  
  // For small number of pages, show all
  if (maxFilteredPage <= 7) {
    return [...Array(maxFilteredPage)].map((_, i) => renderPageButton(i + 1));
  }
  
  // For many pages, show with ellipsis
  const pages = [];
  
  // Always show first page
  pages.push(renderPageButton(1));
  
  // Show ellipsis if needed
  if (currentPage > 3) {
    pages.push(
      <span key="ellipsis-1" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
        ...
      </span>
    );
  }
  
  // Show current page and neighbors
  const startNeighbor = Math.max(2, currentPage - 1);
  const endNeighbor = Math.min(maxFilteredPage - 1, currentPage + 1);
  
  for (let i = startNeighbor; i <= endNeighbor; i++) {
    pages.push(renderPageButton(i));
  }
  
  // Show ellipsis if needed
  if (currentPage < maxFilteredPage - 2) {
    pages.push(
      <span key="ellipsis-2" className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
        ...
      </span>
    );
  }
  
  // Always show last page
  pages.push(renderPageButton(maxFilteredPage));
  
  return pages;
};

// Calculate these values based on filteredVariants

// Add this before your return statement, replacing the existing state variables
// This calculates pagination values based on client-side filtered data
const totalFilteredItems = filteredVariants.length;

// If we're not searching/filtering, use the server's maxPage value, otherwise calculate it locally
const maxFilteredPage = useMemo(() => {
  // If we're searching, calculate locally based on filtered results
  if (searchTerm.trim() !== '') {
    return Math.max(1, Math.ceil(filteredVariants.length / pageSize));
  }
  
  // Otherwise, use the server's maxPage value
  return maxPage;
}, [filteredVariants.length, pageSize, maxPage, searchTerm]);

// Ensure we're not on a page that doesn't exist after filtering
useEffect(() => {
  if (currentPage > maxFilteredPage) {
    setCurrentPage(1);
  }
}, [maxFilteredPage, currentPage, setCurrentPage]);

// This gives us the current page of data to display
const currentPageData = useMemo(() => {
  // When searching, we need to paginate client-side
  if (searchTerm.trim() !== '') {
    const startIndex = (currentPage - 1) * pageSize;
    return filteredVariants.slice(startIndex, startIndex + pageSize);
  }
  
  // When not searching, use the data as it came from the server
  return variants;
}, [filteredVariants, variants, currentPage, pageSize, searchTerm]);

// Add this after the calculation of maxFilteredPage to debug pagination values

useEffect(() => {
  console.log("Pagination debug:", {
    serverMaxPage: maxPage,
    calculatedMaxPage: Math.ceil(totalFilteredItems / pageSize),
    actualMaxUsed: maxFilteredPage,
    searchActive: searchTerm.trim() !== '',
    totalItems, 
    totalFilteredItems,
  });
}, [maxPage, totalFilteredItems, pageSize, maxFilteredPage, searchTerm, totalItems]);

  return (
    <div className="py-6 px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Variantes</h1>
          <p className="mt-2 text-sm text-gray-700">
            Gérer les variantes de produits et leurs valeurs possibles.
          </p>
        </div>
        <div className="mt-4 sm:mt-0">
          <button
            type="button"
            disabled={!hasPermission('write')}
            onClick={handleCreateVariant}
            className="inline-flex items-center px-4 py-2 
            border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#174540] 
            hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] disabled:bg-gray-400 disabled:text-gray-700  disabled:cursor-not-allowed"
            
          >
            <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
            </svg>
            Nouvelle Variante
          </button>
        </div>
      </div>

      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <input
            type="text"
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-[#174540] focus:border-[#174540] sm:text-sm"
            placeholder="Rechercher une variante par nom..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchTerm && (
            <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
              <button
                onClick={() => setSearchTerm('')}
                className="text-gray-400 hover:text-gray-600 focus:outline-none"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="mt-8 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
  {loading ? (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#174540]"></div>
    </div>
  ) : error ? (
    <div className="text-center py-12">
      <div className="text-red-500 mb-2">{error}</div>
      <button
        onClick={() => window.location.reload()}
        className="text-[#174540] hover:underline"
      >
        Réessayer
      </button>
    </div>
  ) : filteredVariants.length === 0 ? (
    <div className="text-center py-12 bg-white">
      <svg
        className="mx-auto h-12 w-12 text-gray-400"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path
          vectorEffect="non-scaling-stroke"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z"
        />
      </svg>
      <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune variante trouvée</h3>
      <p className="mt-1 text-sm text-gray-500">
        {searchTerm ? "Essayez d'autres termes de recherche." : "Commencez par créer une nouvelle variante."}
      </p>
      <div className="mt-6">
        <button
          type="button"
          onClick={handleCreateVariant}
          disabled={!hasPermission('write')}
          className=" disabled:bg-gray-400 disabled:text-gray-700 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-[#174540] hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
        >
          <svg className="-ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Créer une variante
        </button>
      </div>
    </div>
  ) : (
    // Replace standard table with responsive tables
    // Large screens - Show standard table with all columns
    <>
      {/* Desktop version (hidden on small screens) */}
      <div className="hidden md:block">
        <table className="min-w-full divide-y divide-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-16">
                ID
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nom de la Variante
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Valeur
              </th>
              <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Nombre
              </th>
              <th scope="col" className="relative px-4 py-3 w-28">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {currentPageData.map((variant) => (
              <React.Fragment key={variant.id}>
                {/* Variant row */}
                <tr className="hover:bg-gray-50">
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {variant.id}
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    <div className="flex items-center">
                      <button
                        onClick={() => toggleVariantExpansion(variant.id)}
                        className="mr-2 flex items-center focus:outline-none text-gray-600 hover:text-gray-900"
                      >
                        <svg 
                          className={`h-5 w-5 transform ${expandedVariants[variant.id] ? 'rotate-90' : ''} transition-transform duration-150`} 
                          fill="currentColor" 
                          viewBox="0 0 20 20"
                        >
                          <path 
                            fillRule="evenodd" 
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10l-3.293-3.293a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" 
                            clipRule="evenodd" 
                          />
                        </svg>
                      </button>
                      {variant.name}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    -
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                      {variant.values.length}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-3">
                      <button
                      disabled={!hasPermission('edit')}
                        onClick={() => handleEditVariant(variant.id)}
                        className="text-gray-600 hover:text-gray-900  disabled:cursor-not-allowed disabled:text-gray-700"
                        title="Éditer la variante"
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                        </svg>
                      </button>
                      <button
    disabled={!hasPermission('delete')}

                        onClick={() => handleOpenDeleteModal(variant)}
                        className="text-red-600 hover:text-red-900 disabled:cursor-not-allowed disabled:text-gray-700"
                        title="Supprimer la variante" 
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>

                {/* Variant values (nested rows) */}
                {expandedVariants[variant.id] && variant.values.map((value) => (
                  <Transition
                    key={value.id}
                    show={expandedVariants[variant.id]}
                    enter="transition-opacity duration-75"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                  >
                    <tr className="bg-gray-50 hover:bg-gray-100 border-t border-gray-200">
                      <td className="pl-8 pr-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {value.id}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {/* Empty cell for parent name */}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium">
                        {value.value}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        -
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-3">
                          <button
                            className="text-gray-600 hover:text-gray-900"
                            title="Éditer cette valeur"
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => handleDeleteVariantValue(value.id, variant.id)}
                            className="text-red-600 hover:text-red-900 disabled:cursor-not-allowed disabled:text-gray-700"
                            title="Supprimer cette valeur"
                            disabled={!hasPermission('delete')}
                            
                          >
                            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </Transition>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
      
      {/* Mobile version (visible only on small screens) */}
      <div className="md:hidden">
        <ul className="divide-y divide-gray-200 bg-white">
          {currentPageData.map((variant) => (
            <li key={variant.id} className="px-4 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <button
                    onClick={() => toggleVariantExpansion(variant.id)}
                    className="mr-2 flex items-center focus:outline-none text-gray-600"
                  >
                    <svg 
                      className={`h-5 w-5 transform ${expandedVariants[variant.id] ? 'rotate-90' : ''} transition-transform duration-150`} 
                      fill="currentColor" 
                      viewBox="0 0 20 20"
                    >
                      <path 
                        fillRule="evenodd" 
                        d="M7.293 14.707a1 1 0 010-1.414L10.586 10l-3.293-3.293a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" 
                        clipRule="evenodd" 
                      />
                    </svg>
                  </button>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{variant.name}</div>
                    <div className="text-xs text-gray-500">ID: {variant.id}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800">
                    {variant.values.length}
                  </span>
                  
                  <div className="flex space-x-3">
                    <button
                      onClick={() => handleEditVariant(variant.id)}
                      className="text-gray-600 hover:text-gray-900 p-1 disabled:cursor-not-allowed disabled:text-gray-700"
                      title="Éditer la variante"
                      disabled={!hasPermission('edit')}
                      
                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleOpenDeleteModal(variant)}
                      className="text-red-600 hover:text-red-900 p-1 disabled:cursor-not-allowed disabled:text-gray-700"
                      title="Supprimer la variante"
                      disabled={!hasPermission('delete')}

                    >
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Expanded values - mobile view */}
              {expandedVariants[variant.id] && (
                <div className="mt-3 pl-7">
                  <ul className="space-y-2 border-l-2 border-gray-200 pl-4">
                    {variant.values.map((value) => (
                      <li key={value.id} className="py-1">
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="text-sm font-medium text-gray-800">{value.value}</div>
                            <div className="text-xs text-gray-500">ID: {value.id}</div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              className="text-gray-600 hover:text-gray-900 p-1"
                              title="Éditer cette valeur"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                              </svg>
                            </button>
                            <button
                              onClick={() => handleDeleteVariantValue(value.id, variant.id)}
                              className="text-red-600 hover:text-red-900 p-1 disabled:cursor-not-allowed disabled:text-gray-700"
                              title="Supprimer cette valeur"
                              disabled={!hasPermission('delete')}
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
    </>
  )}
</div>

      {/* Pagination */}
{!loading && !error && filteredVariants.length > 0 && (
  <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-4">
    {/* Mobile pagination (visible on small screens) */}
    <div className="flex flex-1 justify-between sm:hidden">
      <button
        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
          currentPage === 1 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        Précédent
      </button>
      <button
        onClick={() => setCurrentPage(Math.min(maxFilteredPage, currentPage + 1))}
        disabled={currentPage === maxFilteredPage}
        className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
          currentPage === maxFilteredPage 
            ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
            : 'bg-white text-gray-700 hover:bg-gray-50'
        }`}
      >
        Suivant
      </button>
    </div>
    
    {/* Desktop pagination */}
    <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
      <div className="flex items-center">
        <p className="text-sm text-gray-700 mr-4">
          Affichage de{' '}
          <span className="font-medium">
            {totalFilteredItems > 0 ? (currentPage - 1) * pageSize + 1 : 0}
          </span>
          {' '}à{' '}
          <span className="font-medium">
            {Math.min(currentPage * pageSize, totalFilteredItems)}
          </span>
          {' '}sur{' '}
          <span className="font-medium">{totalFilteredItems}</span> variantes
        </p>
        
        {/* Page size selector */}
        <div className="flex items-center">
          <label htmlFor="pageSize" className="text-sm text-gray-700 mr-2">
            Afficher :
          </label>
          <select
            id="pageSize"
            name="pageSize"
            value={pageSize}
            onChange={(e) => handlePageSizeChange(e.target.value)}
            className="h-full rounded-md border border-gray-300 bg-white py-0 pl-2 pr-7 text-gray-500 focus:ring-2 focus:ring-inset focus:ring-[#174540] sm:text-sm"
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      {/* Page navigation - make sure to show navigation for more than 1 page */}
      <div>
        <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
          {/* First page button */}
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
              currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
            }`}
            title="Première page"
          >
            <span className="sr-only">Première page</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 010 1.414zm-6 0a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 011.414 1.414L5.414 10l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
          </button>
          
          {/* Previous page button */}
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
              currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
            }`}
            title="Page précédente"
          >
            <span className="sr-only">Précédent</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
          </button>

          {/* Page numbers */}
          {renderPageNumbers()}

          {/* Next page button */}
          <button
            onClick={() => setCurrentPage(Math.min(maxFilteredPage, currentPage + 1))}
            disabled={currentPage === maxFilteredPage}
            className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
              currentPage === maxFilteredPage ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
            }`}
            title="Page suivante"
          >
            <span className="sr-only">Suivant</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
              <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10l-3.293-3.293a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
          
          {/* Last page button */}
          <button
            onClick={() => setCurrentPage(maxFilteredPage)}
            disabled={currentPage === maxFilteredPage}
            className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
              currentPage === maxFilteredPage ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-50'
            }`}
            title="Dernière page"
          >
            <span className="sr-only">Dernière page</span>
            <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 15.707a1 1 0 010-1.414L14.586 10l-4.293-4.293a1 1 0 111.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
              <path fillRule="evenodd" d="M4.293 15.707a1 1 0 010-1.414L8.586 10 4.293 5.707a1 1 0 011.414-1.414l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0z" clipRule="evenodd" />
            </svg>
          </button>
        </nav>
      </div>
    </div>
  </div>
)}

      {/* Add Variant Modal */}
      <Modal
        isOpen={isAddVariantModalOpen}
        onClose={() => setIsAddVariantModalOpen(false)}
        title="Ajouter une nouvelle variante"
        onSubmit={handleSubmitForm} // Changed from null to the function
        size="lg"
        isSubmitting={isSubmitting} // Changed from hardcoded true to state variable
        submitLabel="Créer la variante"
      >
        <AddVariant 
          formId="addVariantForm"
          onSubmit={handleSubmitNewVariant}
          isSubmitting={isSubmitting} // Changed from hardcoded true to state variable
        />
      </Modal>
      
      {/* Delete Variant Modal */}
      <Modal
        isOpen={isDeleteVariantModalOpen}
        onClose={() => {
          if (!isDeleting) {
            setIsDeleteVariantModalOpen(false);
            setVariantToDelete(null);
          }
        }}
        title="Confirmation"
        size="md"
        onSubmit={handleConfirmDeleteVariant}
        isSubmitting={isDeleting}
        submitLabel="Supprimer"
        submitColor="red"
        cancelLabel="Annuler"
      >
        {variantToDelete && (
          <DeleteVariant 
            variant={variantToDelete}
            isDeleting={isDeleting}
          />
        )}
      </Modal>

      {/* Edit Variant Modal */}
      <Modal
        isOpen={isEditVariantModalOpen}
        onClose={() => {
          if (!isEditing) {
            setIsEditVariantModalOpen(false);
            setVariantToEdit(null);
          }
        }}
        title="Modifier la variante"
        size="lg"
        onSubmit={handleSubmitEditForm}
        isSubmitting={isEditing}
        submitLabel="Enregistrer les modifications"
      >
        {variantToEdit && (
          <EditVariant 
            variant={variantToEdit}
            formId="editVariantForm"
            onSubmit={handleSubmitEditVariant}
            isSubmitting={isEditing}
          />
        )}
      </Modal>


    </div>
  );
};

export default VariantsPage;